/* eslint-disable */
// @ts-nocheck
/**
 * FloorMapStudioEngine
 *
 * Wraps the standalone FloorMap Studio JS into a class for React.
 * Uses createFloorMapEngine() which encapsulates all global state in a closure.
 */

import {
  FloorMapEngineConfig,
  FloorMapEngineAPI,
} from './FloorMapStudio.types';
import { iFloorMapData } from 'services/FloorMap/FloorMap.type';
import { createFloorMapEngine, BODY_TEMPLATE } from './createFloorMapEngine';

const STATUS_LABELS: Record<string, string> = {
  TRANSMIS: 'Transmis',
  ENREGISTERED: 'Enregistré',
  ENREGISTRE: 'Enregistré',
  EN_COURSE: 'En cours',
  offre_envoyee: 'Offre envoyée',
};

// Raw status code → readable label (same wording as the exhibitor list page)
function statusLabel(status?: string | null): string {
  if (!status) return '';
  return STATUS_LABELS[status] ?? humanizeCode(status);
}

// "SOME_RAW_CODE" → "Some raw code"
function humanizeCode(value?: string | null): string {
  if (!value) return '';
  const label = String(value).replace(/_/g, ' ').trim();
  return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
}

export class FloorMapStudioEngine implements FloorMapEngineAPI {
  private container: HTMLElement | null = null;
  private _isDirty = false;
  private onDirtyChange?: (isDirty: boolean) => void;
  private onModeChange?: (mode: 'px' | 'vc') => void;
  private onSave?: () => void;
  private engine: any = null;

  public init(config: FloorMapEngineConfig): void {
    this.container = config.container;
    this.onDirtyChange = config.onDirtyChange;
    this.onModeChange = config.onModeChange;
    this.onSave = config.onSave;

    // 1. Insert DOM structure
    this.container.innerHTML = BODY_TEMPLATE;

    // 2. Map exhibitors to engine format
    const expos = (config.exhibitors || []).map((ex) => {
      // Extract unique product types from showroom products
      const products = ex.exhibitor_events_showroom_products || [];
      const productTypes = [...new Set(
        products
          .map((p: any) => p?.product?.type_produit || p?.type_produit || '')
          .filter((t: string) => t)
          .map((t: string) => {
            // Convert enum-style codes to readable labels
            const label = t.replace(/^type_produit:/, '').replace(/_/g, ' ');
            return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
          })
      )];
      return {
        id: ex.exhibitor_id,
        name: ex.exhibitor_nom_societe || ex.exhibitor_raison_sociale || '',
        country: ex.exhibitor_hall || '',
        stand: ex.exhibitor_stand || '',
        hall: ex.exhibitor_hall || '',
        tag: '',
        tagL: ex.collectif || ex.exhibitor_collectif_type || '',
        participation: ex.collectif || '',
        statut: statusLabel(ex.status),
        categorie: humanizeCode(ex.thematiques),
        typeProduit: productTypes.join(', '),
      };
    });

    // 3. Initialize engine (patches document.getElementById, inits canvases)
    try {
      this.engine = createFloorMapEngine(this.container, expos);
      if (this.engine) {
        this.engine.onDirtyChange = (dirty: boolean) => {
          this._isDirty = dirty;
          this.onDirtyChange?.(dirty);
        };
      }
    } catch (e) {
      console.error('FloorMapStudioEngine init error:', e);
    }

    // 3. Load initial data if provided
    if (config.initialData) {
      this.loadData(config.initialData);
    }
  }

  public destroy(): void {
    if (this.engine) {
      this.engine.destroy();
      this.engine = null;
    }

    if (this.container) {
      this.container.innerHTML = '';
    }

    this.container = null;
  }

  public serialize(): Partial<iFloorMapData> {
    if (!this.engine) return {};

    // Auto-sync: Vec→Pixel when in vector mode + persist mod layers
    this.engine.prepareForSave();

    const state = this.engine.getState();
    const vcJSON = this.engine.getVcJSON();

    const modLayers = state.MOD_LAYERS?.map((layer: any) => ({
      id: layer.id,
      name: layer.name,
      isPrincipal: layer.isPrincipal,
      grid: layer.grid,
      sepH: layer.sepH,
      sepV: layer.sepV,
      vcJSON: layer.vcJSON,
      vcAssigns: layer.vcAssigns,
      typeState: layer.typeState,
      vcStandN: layer.vcStandN,
      vcGroups: layer.vcGroups,
    }));

    return {
      hall_width: state.hallW,
      hall_height: state.hallH,
      cell_size: state.cellM,
      grid_data: state.grid,
      separators_data: { sepH: state.sepH, sepV: state.sepV },
      vector_data: vcJSON,
      stand_types_data: state.RTYPES,
      assignments_data: state.vcAssigns,
      groups_data: state.vcGroups,
      layers_data: modLayers,
      moderator_data: { activeModLayer: state.activeModLayer },
      current_mode: state.activeTab === 'vc' ? 'vector' : 'pixel',
    };
  }

  /** Stands of the vector canvas, for assigning them to floor map layers. */
  public getStands(): { id: string; label: string; exhibitor: string }[] {
    if (!this.engine) return [];
    const vcJSON = this.engine.getVcJSON();
    const assigns = this.engine.getState().vcAssigns || {};
    const stands: { id: string; label: string; exhibitor: string }[] = [];
    (vcJSON?.objects || []).forEach((o: any) => {
      const d = o.sData;
      if (!d?.id || d.type === 'aisle' || d.type === 'wall') return;
      stands.push({
        id: d.id,
        label: `${d.hall || ''}${d.num || ''}`,
        exhibitor: assigns[d.id]?.expo?.name || '',
      });
    });
    return stands;
  }

  /** Id of the forker (moderator layer) currently active in the editor. */
  public getActiveForkerId(): string | null {
    if (!this.engine) return null;
    const state = this.engine.getState();
    return state.MOD_LAYERS?.[state.activeModLayer]?.id ?? null;
  }

  public loadData(data: iFloorMapData): void {
    if (!this.engine) return;

    const stateUpdate: Record<string, any> = {};

    if (data.hall_width) stateUpdate.hallW = data.hall_width;
    if (data.hall_height) stateUpdate.hallH = data.hall_height;
    if (data.cell_size) stateUpdate.cellM = data.cell_size;
    if (data.grid_data) stateUpdate.grid = data.grid_data;
    if (data.separators_data) {
      stateUpdate.sepH = data.separators_data.sepH || [];
      stateUpdate.sepV = data.separators_data.sepV || [];
    }
    if (data.stand_types_data) stateUpdate.RTYPES = data.stand_types_data;
    if (data.assignments_data) stateUpdate.vcAssigns = data.assignments_data;
    if (data.groups_data) stateUpdate.vcGroups = data.groups_data;
    if (data.layers_data) stateUpdate.MOD_LAYERS = data.layers_data;
    if (data.moderator_data && data.moderator_data.activeModLayer != null) {
      stateUpdate.activeModLayer = data.moderator_data.activeModLayer;
    }

    this.engine.setState(stateUpdate);

    if (data.vector_data) {
      this.engine.loadVcJSON(data.vector_data);
    }

    // Restore active mode (pixel/vector) if saved
    {
      const api = this.engine as any;
      const target: 'px' | 'vc' = data.current_mode === 'vector' ? 'vc' : 'px';
      if (typeof api.switchTab === 'function') {
        if (target === 'vc') {
          setTimeout(() => {
            api.switchTab('vc');
            this.onModeChange?.('vc');
          }, 50);
        } else {
          api.switchTab('px');
          this.onModeChange?.('px');
        }
      }
    }

    this._isDirty = false;
    this.onDirtyChange?.(false);
  }

  public updateExhibitors(exhibitors: any[]): void {
    if (!this.engine) return;

    const expos = (exhibitors || []).map((ex) => {
      const products = ex.exhibitor_events_showroom_products || [];
      const productTypes = [...new Set(
        products
          .map((p: any) => p?.product?.type_produit || p?.type_produit || '')
          .filter((t: string) => t)
          .map((t: string) => {
            const label = t.replace(/^type_produit:/, '').replace(/_/g, ' ');
            return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
          })
      )];
      return {
        id: ex.exhibitor_id,
        name: ex.exhibitor_nom_societe || ex.exhibitor_raison_sociale || '',
        country: ex.exhibitor_hall || '',
        stand: ex.exhibitor_stand || '',
        hall: ex.exhibitor_hall || '',
        tag: '',
        tagL: ex.collectif || ex.exhibitor_collectif_type || '',
        participation: ex.collectif || '',
        statut: statusLabel(ex.status),
        categorie: humanizeCode(ex.thematiques),
        typeProduit: productTypes.join(', '),
      };
    });

    this.engine.updateExpos(expos);
  }

  public resize(): void {
    if (this.engine && typeof this.engine.resize === 'function') {
      this.engine.resize();
    }
  }

  public isDirty(): boolean {
    return this._isDirty;
  }
}
